import React from 'react';
import styles from './Select.module.scss';
import { Text } from '../../Typography/Typography';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps<T extends string>
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  options: SelectOption[];
  label?: string;
  value?: T;
  onChange: (value: T | undefined) => void;
  fullWidth?: boolean;
  labelColor?: string;
  isClearable?: boolean;
  error?: string;
  placeholder?: string;
}

const Select = <T extends string>({
  options,
  label,
  value,
  onChange,
  fullWidth = false,
  className,
  id,
  labelColor = 'neutral-600',
  isClearable = false,
  error,
  placeholder,
  ...props
}: SelectProps<T>) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    onChange(newValue === '' ? undefined : (newValue as T));
  };

  const selectClasses = [
    styles.select,
    fullWidth ? styles.fullWidth : '',
    error ? styles.error : '',
    className || '',
  ].join(' ');

  const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className={`${styles.selectContainer} ${fullWidth ? styles.fullWidth : ''}`}>
      {label && (
        <label htmlFor={selectId} className={styles.labelWrapper}>
          <Text size="small" weight="medium" color={labelColor}>
            {label}
          </Text>
        </label>
      )}
      <select
        id={selectId}
        className={selectClasses}
        value={value || ''}
        onChange={handleChange}
        {...props}
      >
        {isClearable && <option value="">{placeholder || 'Select...'}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <Text size="small" color="red" className={styles.errorMessage}>
          {error}
        </Text>
      )}
    </div>
  );
};

export default Select;
